import type { IApiResponse } from "..";

/**
 * Which dashboard a layout belongs to. Mirrors the API's `DashboardScope` enum —
 * one endpoint serves both canvases, told apart by this value.
 */
export const DASHBOARD_SCOPE = {
  WORKSPACE: "workspace",
  MY_SPACE: "my_space",
  /**
   * The Workspace dashboard's quick-actions bar, not a canvas: each "widget" is
   * one pinned action (`type` = the action's catalogue key), in bar order.
   */
  WORKSPACE_QUICK_ACTIONS: "workspace_quick_actions",
} as const;

export type TDashboardScope =
  (typeof DASHBOARD_SCOPE)[keyof typeof DASHBOARD_SCOPE];

/**
 * One widget on the canvas. `type` and `size` are registry keys, not API
 * vocabulary — the server round-trips them without validating them against a
 * catalogue, which is why the client must tolerate values it no longer knows.
 */
export interface IDashboardWidgetPlacement {
  instanceId: string;
  type: string;
  size: string;
}

/** The stored arrangement. Position comes from array order + each widget's size. */
export interface IDashboardLayoutPayload {
  version: number;
  widgets: IDashboardWidgetPlacement[];
}

/** Where a returned layout came from. Mirrors the API's `DashboardLayoutSource`. */
export type TDashboardLayoutSource = "user" | "fallback";

/** `isCustomized: false` means the canvas is on the built-in default. */
export interface IDashboardLayoutData {
  scope: TDashboardScope;
  isCustomized: boolean;
  source: TDashboardLayoutSource;
  layout: IDashboardLayoutPayload | null;
  updatedAt: string | null;
}

export type TDashboardLayoutResponse = IApiResponse<IDashboardLayoutData>;

export interface ISaveDashboardLayoutRequest extends IDashboardLayoutPayload {
  scope: TDashboardScope;
}
